import { z } from 'zod';
import {
  AccountScopeSchema,
  CursorInputSchema,
  IdSchema,
  IdempotencyKeySchema,
  IsoDateTimeSchema,
  MoneySchema,
  RangeSchema,
  pageOf,
  withMeta,
} from '../common';
import { AttrSchema, ScoreMovedSchema, ScorePillarSchema, StatSchema } from '../dataset/core';
import { AiDataSchema } from '../dataset/ai';
import { AnalyticsDataSchema } from '../dataset/analytics';
import { CompareDataSchema } from '../dataset/compare';
import { DigestDataSchema } from '../dataset/digest';
import { ScoreDataSchema } from '../dataset/score';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** analyse.* — Analytics, Compare Accounts, Trading Intelligence, Performance Digest, PointFive Score, AI Insights
 *  (SDD §7.4 Analyse rows). Every figure equals its named §11.5 aggregate; the UI never recalculates scores. */

const scopeInput = z
  .object({ range: RangeSchema.optional(), accountId: AccountScopeSchema.optional() })
  .strict()
  .optional();

// ── Analytics ──
export const AnalyticsMetricsSchema = withMeta({
  data: AnalyticsDataSchema,
  stats: z.array(StatSchema),
});
export const DistributionBucketSchema = z
  .object({ label: z.string(), count: z.number().int(), pnl: MoneySchema })
  .strict();
export const AnalyticsSplitsSchema = withMeta({
  bySymbol: z.array(AttrSchema),
  byStrategy: z.array(AttrSchema),
  bySession: z.array(AttrSchema),
});
export const HeatCellSchema = z
  .object({ row: z.string(), col: z.string(), value: z.number(), n: z.number().int() })
  .strict();

// ── Compare ──
export const CompareResultSchema = withMeta({
  roster: CompareDataSchema,
  selected: z.array(z.string()),
  /** Server-computed deltas per metric between the selected accounts (min-2 selection gate). */
  deltas: z.array(
    z
      .object({ metric: z.string(), values: z.record(z.string(), z.number()), leader: z.string().nullable() })
      .strict(),
  ),
});

// ── Trading intelligence ──
export const FindingSchema = z
  .object({
    id: IdSchema,
    title: z.string(),
    body: z.string(),
    significance: z.number().min(0).max(1),
    suppressed: z.boolean(),
    evidence: z.array(
      z.object({ kind: z.enum(['trade', 'session', 'aggregate']), ref: z.string() }).strict(),
    ),
    createdAt: IsoDateTimeSchema,
    dismissedAt: IsoDateTimeSchema.nullable(),
  })
  .strict();

// ── Digest ──
export const DigestPrefsSchema = z
  .object({
    cadence: z.enum(['weekly', 'monthly']),
    day: z.number().int().min(0).max(6),
    sections: z.array(z.string()),
    accounts: z.array(z.string()),
  })
  .strict();
export const DigestListSchema = withMeta({ data: DigestDataSchema, prefs: DigestPrefsSchema });
export const DigestDetailSchema = withMeta({
  digestId: IdSchema,
  period: z.string(),
  range: z.string(),
  summary: z.string(),
  aiOutlook: z.string(),
  kpis: z.array(z.object({ label: z.string(), value: z.string() }).strict()),
});

// ── Score (v1.4 normative) ──
export const ScoreSnapshotSchema = withMeta({
  data: ScoreDataSchema,
  pillars: z.array(ScorePillarSchema),
  /** Headline figure as the engine computed it (raw vs displayed tier are both carried). */
  headline: z
    .object({
      score: z.number(),
      tier: z.string(),
      rawScore: z.number(),
      /** 0–100 under v1.4 (§4.5), not the 0–1 fraction v1.3 carried. */
      confidence: z.number(),
      percentile: z.number(),
      rank: z.number().int(),
      /** The tier band a trader would hold at full confidence, and how many bands the engine withheld. Both, so a
       *  provisional score reads as provisional rather than as a verdict. */
      rawTier: z.string(),
      tierReducedBy: z.number().int(),
      /** §13.3. Eligibility is a gate on top of the score, never a component of it — a high score that fails the
       *  gate is still a high score, and the card must say which of the two it is. */
      capitalEligible: z.boolean(),
      capitalGate: z
        .object({
          tier: z.boolean(),
          confidence: z.boolean(),
          tailRatio: z.boolean(),
          crisisBeta: z.boolean(),
          shortVol: z.boolean(),
        })
        .strict(),
      /** §13.4. A short-volatility signature is a shape of return, not a fault — labelled so it can be read. */
      riskProfile: z.enum(['standard', 'short_volatility']),
      /** Owner directive 2026-08-27 §8. Carried beside the score rather than derived from `confidence`, because
       *  the screen has to render "Calibrating — N/500" and a percentage cannot be turned back into a count.
       *  `scoreVersion` travels with them so a score a trader was shown last month stays auditable against the
       *  model that produced it rather than being reinterpreted by whatever is current. */
      calibrationState: z.enum(['provisional', 'calibrated']),
      eligibleTrades: z.number().int(),
      calibrationThreshold: z.number().int(),
      scoreVersion: z.string(),
    })
    .strict(),
});
export const ScoreHistoryPointSchema = z
  .object({ month: z.string(), score: z.number(), tier: z.string() })
  .strict();
export const PillarDetailSchema = withMeta({
  pillarId: z.string(),
  name: z.string(),
  value: z.number(),
  max: z.number(),
  metrics: z.array(
    z
      .object({
        name: z.string(),
        points: z.number(),
        max: z.number(),
        raw: z.string(),
        adjusted: z.string(),
        /** §4.5's two absence classes, kept apart on the wire. `dataLimited` is "you have not traded enough yet";
         *  `inputMissing` is "your broker never sent this". They cost different confidence and mean different
         *  things to the trader, so collapsing them into one flag would mislead. */
        dataLimited: z.boolean(),
        inputMissing: z.boolean(),
      })
      .strict(),
  ),
});
export const ScoreGoalSchema = z
  .object({ pillarId: z.string(), target: z.number(), by: z.string() })
  .strict();
export const ShareCardSchema = withMeta({
  token: z.string(),
  url: z.string(),
  createdAt: IsoDateTimeSchema,
  revokedAt: IsoDateTimeSchema.nullable(),
  views: z.number().int(),
  /** Investors see verified figures only; pillars stripped for ungated viewers. */
  preview: z
    .object({
      name: z.string(),
      score: z.number(),
      tier: z.string(),
      pillars: z.array(ScorePillarSchema).nullable(),
    })
    .strict(),
});
export const LeaderboardSchema = withMeta({
  population: z.number().int(),
  rank: z.number().int(),
  percentile: z.number(),
  rows: z.array(
    z
      .object({
        rank: z.number().int(),
        handle: z.string(),
        score: z.number(),
        tier: z.string(),
        you: z.boolean(),
      })
      .strict(),
  ),
});
export const ConfidenceSchema = withMeta({
  /** 0–100 (§4.5). */
  confidence: z.number(),
  eligibleTrades: z.number().int(),
  months: z.number().int(),
  /** §4.2 — decay-weighted sample sizes. A trader with 400 trades from two years ago has a large `eligibleTrades`
   *  and a small `effectiveTrades`, and the second is the one the shrinkage actually used. */
  effectiveTrades: z.number(),
  effectiveDays: z.number(),
  dataLimitedCount: z.number().int(),
  inputMissingCount: z.number().int(),
  band: z.enum(['low', 'medium', 'high']),
  /** The same four fields the headline carries. Duplicated deliberately: this is the card that answers "why is my
   *  score provisional?", and it must be able to answer without a second request. */
  calibrationState: z.enum(['provisional', 'calibrated']),
  calibrationThreshold: z.number().int(),
  scoreVersion: z.string(),
});

// ── Insights ──
export const InsightsSchema = withMeta({
  data: AiDataSchema,
  styleHeader: z.object({ style: z.string(), experience: z.string() }).strict(),
});

export const analyseProcedures = defineProcedures({
  'analyse.analytics.metrics': query(
    'Headline metrics + analytics inputs for a scope',
    scopeInput,
    AnalyticsMetricsSchema,
  ),
  'analyse.analytics.distribution': query(
    'P&L / R distribution buckets',
    scopeInput,
    withMeta({ buckets: z.array(DistributionBucketSchema) }),
  ),
  'analyse.analytics.splits': query(
    'Splits by symbol / strategy / session',
    scopeInput,
    AnalyticsSplitsSchema,
  ),
  'analyse.analytics.proHeatmap': query(
    'Pro: session × hour heatmap',
    scopeInput,
    withMeta({ cells: z.array(HeatCellSchema) }),
  ),
  'analyse.analytics.proScatter': query(
    'Pro: entry-hour scatter',
    scopeInput,
    withMeta({
      points: z.array(z.object({ hour: z.number(), r: z.number(), id: z.number().int() }).strict()),
    }),
  ),
  'analyse.analytics.proSizeBands': query(
    'Pro: size bands',
    scopeInput,
    withMeta({ bands: z.array(DistributionBucketSchema) }),
  ),
  'analyse.analytics.proPlanAdherence': query(
    'Pro: on-plan vs off-plan',
    scopeInput,
    withMeta({ onPlan: DistributionBucketSchema, offPlan: DistributionBucketSchema }),
  ),

  'analyse.compare.compare': query(
    'Compare accounts side by side (min 2; server-computed deltas)',
    z
      .object({ accountIds: z.array(z.string()).max(8) })
      .strict()
      .optional(),
    CompareResultSchema,
  ),

  'analyse.intel.list': query(
    'Evidence-linked findings',
    CursorInputSchema.optional(),
    pageOf(FindingSchema),
  ),
  'analyse.intel.get': query('One finding', z.object({ id: IdSchema }).strict(), FindingSchema),
  'analyse.intel.dismiss': mutation('Dismiss a finding', z.object({ id: IdSchema }).strict(), FindingSchema),

  'analyse.digest.list': query('Digest history + prefs', z.void(), DigestListSchema),
  'analyse.digest.get': query('One digest', z.object({ digestId: IdSchema }).strict(), DigestDetailSchema),
  'analyse.digest.prefs': query('Digest preferences', z.void(), withMeta({ prefs: DigestPrefsSchema })),
  'analyse.digest.updatePrefs': mutation(
    'Update digest preferences',
    DigestPrefsSchema.partial(),
    withMeta({ prefs: DigestPrefsSchema }),
  ),
  'analyse.digest.forward': mutation(
    'Forward a digest by email',
    z.object({ idempotencyKey: IdempotencyKeySchema, digestId: IdSchema, to: z.email() }).strict(),
    withMeta({ ok: z.literal(true) }),
  ),
  'analyse.digest.archive': mutation(
    'Archive a digest',
    z.object({ digestId: IdSchema }).strict(),
    DigestListSchema,
  ),

  'analyse.score.get': query(
    'PointFive Score snapshot (spec inputs + pillars + headline)',
    z.object({ accountId: AccountScopeSchema.optional() }).strict().optional(),
    ScoreSnapshotSchema,
  ),
  'analyse.score.history': query(
    'Score history by month',
    z.object({ accountId: AccountScopeSchema.optional() }).strict().optional(),
    withMeta({ points: z.array(ScoreHistoryPointSchema) }),
  ),
  'analyse.score.pillarDetail': query(
    'Pillar drill-down to sub-metrics',
    z.object({ pillarId: z.string() }).strict(),
    PillarDetailSchema,
  ),
  'analyse.score.movers': query(
    'Movers ranked by pillar impact',
    z.void(),
    withMeta({ items: z.array(ScoreMovedSchema) }),
  ),
  'analyse.score.goals': query('Score goals', z.void(), withMeta({ items: z.array(ScoreGoalSchema) })),
  'analyse.score.updateGoals': mutation(
    'Set score goals',
    z.object({ items: z.array(ScoreGoalSchema).max(8) }).strict(),
    withMeta({ items: z.array(ScoreGoalSchema) }),
  ),
  'analyse.score.shareCard': mutation(
    'Create (or fetch) the public share card',
    z.object({ idempotencyKey: IdempotencyKeySchema, revoke: z.boolean().optional() }).strict(),
    ShareCardSchema,
  ),
  'analyse.score.leaderboard': query('Leaderboard context', z.void(), LeaderboardSchema),
  'analyse.score.confidence': query('Confidence band', z.void(), ConfidenceSchema),

  'analyse.insights.list': query('AI insights + style header', z.void(), InsightsSchema),
  'analyse.insights.rate': mutation(
    'Rate an insight',
    z.object({ index: z.number().int().nonnegative(), rating: z.enum(['up', 'down']) }).strict(),
    InsightsSchema,
  ),
  'analyse.insights.dismiss': mutation(
    'Dismiss an insight',
    z.object({ index: z.number().int().nonnegative() }).strict(),
    InsightsSchema,
  ),
  'analyse.insights.sendToSession': mutation(
    'Send an action-plan item to the next session (suggestion.created)',
    z.object({ idempotencyKey: IdempotencyKeySchema, text: z.string().min(1).max(280) }).strict(),
    withMeta({ suggestionId: IdSchema }),
  ),
});
export type AnalyseFamilyClient = RouterShape<typeof analyseProcedures>;

export const analyseFamily = defineFamily({
  name: 'analyse',
  title: 'Analyse',
  screens: [
    'Analytics',
    'Compare Accounts',
    'Trading Intelligence',
    'Performance Digest',
    'PointFive Score',
    'AI Insights',
  ],
  procedures: analyseProcedures,
  binding: 'simulated',
});
